// Blueprint: crap/blueprint/workspace/architecture/001-coalesce-workspace-events.md
import * as Telecom from '@servicetitan/telecom-rtcs-client';
import {
  ServiceUserStats,
  userStatsFromAgentStats,
} from '../../types/ServiceUser';
import requireUser, {
  UserNotFoundError,
  rosterUser,
} from '../../lib/requireUser';
import log, { logException } from '../../lib/log';
import isValidUser from '../../lib/isValidUser';
import { ServiceUserUtils } from '../../lib/serviceUserUtils';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import { setUserStatus } from '../../lib/userStatus';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import ServiceModule from '@modules/ServiceModule';
import { recordAgentStatusChange } from '../../../metrics/agentStatusChangeHelpers';
import { Source } from '../../../metrics/agentMetrics';
import { serviceState } from '../../lib/state';
import {
  isStaleAfterLogout,
  resetAgentLogoutGuard,
} from '../../lib/agentLogoutGuard';
import featureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import {
  dropCoalescedPendingAgentStatus,
  enqueueAgentStatusEvent,
} from '../../lib/coalesceWorkspaceEvents';

// RTCS broadcasts agent.status account-wide with no server-side filtering, so a large
// account can deliver ~100+ non-self events in the same instant. Each is applied
// synchronously by applyAgentStatus, so a burst becomes a single long-blocking task
// (CCPE-1920). Coalescing buffers non-self events and flushes them together so the
// resulting valtio mutations land in one render instead of one per event.
const FLUSH_DELAY_MS = 1000;

const pending = new Map<string, Telecom.AgentStatusEvent>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
// applyAgentStatus awaits requireUser, which hits the network when the agent is missing
// from the roster cache — routine, since every new login broadcasts account-wide. The
// flush runs on a bare timer rather than the EventQueue, so without these handles nothing
// would await those writes and they could land on a session that has since been torn
// down or resynced. A set rather than a single handle because flushPending clears
// flushTimer up front: a slow flush (many uncached lookups) can still be fetching when
// the next one fires, and both have to stay drainable.
const inFlightFlushes = new Set<Promise<void>>();

function isStaleSnapshot(event: Telecom.AgentStatusEvent) {
  if (!isStaleAfterLogout(event.agentId, event.timestamp)) {
    return false;
  }
  log('rtcs:agent:status:stale-after-logout', event.agentId);
  return true;
}

function commitAgentStatus(
  event: Telecom.AgentStatusEvent,
  preparedUser: Awaited<ReturnType<typeof requireUser>>,
) {
  const user = rosterUser(event.agentId, preparedUser);
  log('rtcs:agent:status:' + user?.name, event);

  const isUserAvailable = ServiceUserUtils.isUserAvailable(user);
  const isUsesStatusUpdateInProgress = ServiceModule.getUserStatusUpdateLoading(
    user.id,
  );

  // Capture current status for metrics (before change)
  const previousStatus = ServiceUserUtils.getAvailabilityStatus(user);

  // If user is available set status to Available, if user not Available
  if (event.available && !isUserAvailable) {
    setUserStatus({
      userId: user.id,
      status: UserSetAvailabilityStatus.Available,
    });

    // Track automatic status change (RTCS-initiated)
    recordAgentStatusChange(
      previousStatus,
      UserSetAvailabilityStatus.Available,
      Source.AUTOMATIC,
    );
  } else if (
    !event.available &&
    isUserAvailable &&
    !isUsesStatusUpdateInProgress
  ) {
    const message = `User wrong status: User ${user.id} is Available but received available=false from RTCS.`;
    DiagnosticsModule.reportUserError({
      message: message,
      priority: 'direct',
    });
  }

  user.stats = event.stats
    ? userStatsFromAgentStats(event.stats)
    : ({} as ServiceUserStats);

  ServiceUserUtils.setEventActivityStatus(user, event.availabilityState);
}

function isUnresolvedBroadcastAgent(
  err: unknown,
  event: Telecom.AgentStatusEvent,
): boolean {
  return (
    err instanceof UserNotFoundError &&
    event.agentId !== serviceState.contactCenterUserId
  );
}

async function prepareAgentStatus(event: Telecom.AgentStatusEvent) {
  // Checked here so snapshots already buffered are dropped at flush too.
  if (isStaleSnapshot(event)) {
    return undefined;
  }

  let user;
  try {
    user = await requireUser(event.agentId);
  } catch (err) {
    // agent.status is broadcast account-wide, so an agent absent from this session's
    // roster is expected, not an error. Reporting it flooded Sentry (CCPE-2640). Self
    // still throws: the current agent missing from its own roster is a real fault.
    if (isUnresolvedBroadcastAgent(err, event)) {
      log('rtcs:agent:status:unresolved-agent', event.agentId);
      return undefined;
    }
    throw err;
  }

  // The roster fetch above can outlast a logout, so check again.
  if (isStaleSnapshot(event)) {
    return undefined;
  }
  return user;
}

async function applyAgentStatus(event: Telecom.AgentStatusEvent) {
  // Same hop as master / handleAgentAway: await requireUser then write here.
  // `await prepareAgentStatus()` then commit adds a microtask and can lose to
  // force-away in the same SignalR receive.
  if (isStaleSnapshot(event)) {
    return;
  }

  let user;
  try {
    user = await requireUser(event.agentId);
  } catch (err) {
    if (isUnresolvedBroadcastAgent(err, event)) {
      log('rtcs:agent:status:unresolved-agent', event.agentId);
      return;
    }
    throw err;
  }

  if (isStaleSnapshot(event)) {
    return;
  }
  commitAgentStatus(event, user);
}

function flushPending() {
  flushTimer = null;
  const batch = [...pending.values()];
  pending.clear();
  const applied = batch
    .filter(
      // contactCenterUserId is null until openWorkspace resolves it, so a self
      // event can still reach the buffer. Self status is applied synchronously
      // and drives routing — never let a buffered snapshot overwrite it.
      (event) => event.agentId !== serviceState.contactCenterUserId,
    )
    .map((event) =>
      applyAgentStatus(event).catch((err) =>
        logException(err, 'rtcs:agent:status:flush'),
      ),
    );

  if (applied.length === 0) {
    return;
  }

  const settled = Promise.all(applied).then(() => {
    inFlightFlushes.delete(settled);
  });
  inFlightFlushes.add(settled);
}

/**
 * Drops buffered events, cancels the pending flush, and waits for every flush already in
 * flight to finish writing. Call on workspace close and before a resync writes a fresh
 * roster, so a new session can't apply stale events from the previous one.
 *
 * Awaiting matters as much as clearing: a flush that has already fired is suspended on
 * requireUser's network fetch and is not on the EventQueue, so nothing else waits for it.
 * Callers must drain it *before* tearing down or overwriting serviceState, otherwise the
 * late writes land on state that has already moved on.
 *
 * Note this clears the module-level buffer, which is shared across sessions. Callers must
 * confirm the session is still theirs before calling, or they will discard a successor's
 * buffered events without writing anything in their place.
 */
export async function resetPendingAgentStatus(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
  }
  flushTimer = null;
  pending.clear();
  resetAgentLogoutGuard();
  await Promise.allSettled([...inFlightFlushes]);
}

/**
 * Drops the buffered status for one agent. Sibling `agent.*` handlers run synchronously
 * through the EventQueue, so without this a queued snapshot would flush ~1s later and
 * revert them.
 *
 * Siblings write availability *or* activity, not both: `agent.ready` / `agent.away` write
 * availability and no activity, while the session and external-call handlers write
 * activity and no availability. So the drop discards whichever fields the sibling does not
 * replicate. Either loss is bounded by the next status heartbeat and surfaces on
 * `RTCS_AGENT_STATUS_ACCURACY_TOTAL`.
 *
 * Kept broad on purpose. `applyAgentStatus` only ever *promotes* to Available — its
 * demotion branch reports a diagnostic and writes nothing — so a dropped snapshot can only
 * lose a promotion, erring toward showing an available agent as unavailable. Narrowing the
 * drop to just the availability events would instead let a stale snapshot reset
 * `activityStatus` over a newer `CallOffer`, making a busy agent look idle: the unsafe
 * direction.
 */
export function dropPendingAgentStatus(agentId: string) {
  pending.delete(agentId);
  dropCoalescedPendingAgentStatus(agentId);
}

async function handleAgentStatus(event: Telecom.AgentStatusEvent) {
  if (!isValidUser(event.agentId)) {
    return;
  }

  const coalesceEnabled = featureFlagModule.isEnabled(
    FeatureFlagKeys.featureCoalesceAgentStatusUpdates,
  );
  const workspaceCoalesce = featureFlagModule.isEnabled(
    FeatureFlagKeys.featureCoalesceWorkspaceEvents,
  );
  const isSelf = event.agentId === serviceState.contactCenterUserId;

  if (!coalesceEnabled || isSelf) {
    return applyAgentStatus(event);
  }

  if (workspaceCoalesce) {
    return enqueueAgentStatusEvent(event.agentId, {
      prepare: () => prepareAgentStatus(event),
      commit: (user) => commitAgentStatus(event, user),
    });
  }

  pending.set(event.agentId, event);
  flushTimer ??= setTimeout(flushPending, FLUSH_DELAY_MS);
}

export default handleAgentStatus;
